import './brokerage-list.scss';

import {
  Box,
  Typography,
  Drawer,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Autocomplete,
  TextField,
  Button,
} from '@mui/material';
import { Cancel, FilterList, ExpandMore } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import DateRangeIcon from '@mui/icons-material/DateRange';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  EntityStatus,
  useDebounce,
  useGetEntitiesBrokeragesOptions,
  useGetRegion,
} from '@converge/graphql';
import {
  CheckedState,
  FilterState,
  Option,
} from '../../../../presentation/components/table-filter';
import { useInfiniteScrollFilter } from '../../../../hooks';
import { highlightTextForFilter } from '../../utils';
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { NewConvergeButton } from '../../../../presentation';

export interface BrokerageFilterOptions {
  brokerage: Option[];
  parentEntity: Option[];
  partnerEntity: Option[];
  uwRegion: Option[];
  status: Option[];
}

const statusOptions = [
  { label: 'Active', value: EntityStatus.Active },
  { label: 'Inactive', value: EntityStatus.Inactive },
];

const defaultFilters = {
  brokerage: [] as string[],
  parentEntity: [] as string[],
  partnerEntity: [] as string[],
  uwRegion: [] as string[],
  onboardedDate: [] as string[],
  status: [] as string[],
};

interface BrokerageFilterProps {
  currentFilters: CheckedState;
  setCurrentFilters: (filters: CheckedState) => void;
  currentFilterLabels: CheckedState;
  setCurrentFilterLabels: (filters: CheckedState) => void;
  filterLabels: CheckedState;
  setFilterLabels: (filters: CheckedState) => void;
  setBrokerageFilters?: (filters: CheckedState) => void;
  setDefaultBrokerages?: (option: Option[]) => void;
  resetPageNumber: () => void;
}

const BrokerageFilter = ({
  setBrokerageFilters,
  setDefaultBrokerages,
  currentFilters,
  setCurrentFilters,
  currentFilterLabels,
  setCurrentFilterLabels,
  filterLabels,
  setFilterLabels,
  resetPageNumber,
}: BrokerageFilterProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const [pendingFilters, setPendingFilters] =
    useState<CheckedState>(currentFilters);
  const [pendingFilterLabels, setPendingFilterLabels] =
    useState<CheckedState>(currentFilterLabels);

  const [expandedPanels, setExpandedPanels] = useState<{
    [key: string]: boolean;
  }>({
    brokerage: false,
    parentEntity: false,
    partnerEntity: false,
    uwRegion: false,
    onboardedDate: false,
    status: false,
  });

  const [onboardedDateRange, setOnboardedDateRange] = useState<
    [Dayjs | null, Dayjs | null]
  >([null, null]);
  const [onboardedDateError, setOnboardedDateError] = useState('');

  const emptyOptions: BrokerageFilterOptions = {
    brokerage: [],
    parentEntity: [],
    partnerEntity: [],
    uwRegion: [],
    status: [],
  };

  const [allOptions, setAllOptions] =
    useState<BrokerageFilterOptions>(emptyOptions);
  const [filterOptions, setFilterOptions] =
    useState<BrokerageFilterOptions>(emptyOptions);

  const [searchFilters, setSearchFilters] = useState<FilterState>({
    brokerage: '',
    parentEntity: '',
    partnerEntity: '',
    uwRegion: '',
    status: '',
  });

  const debouncedBrokerageName = useDebounce<string>(
    searchFilters['brokerage'],
    300
  );
  const debouncedParentEntityName = useDebounce<string>(
    searchFilters['parentEntity'],
    300
  );
  const debouncedPartnerEntityName = useDebounce<string>(
    searchFilters['partnerEntity'],
    300
  );

  // Fetch brokerage options with infinite scroll
  const {
    loading: brokerageListLoading,
    hasNextPage: hasMoreBrokerages,
    fetchMore: fetchMoreBrokerages,
    data: brokerageData,
  } = useGetEntitiesBrokeragesOptions({
    pageNumber: 0,
    pageSize: 50,
    enabled: modalOpen,
    brokerageName: debouncedBrokerageName,
  });

  // Fetch parent entity options with infinite scroll
  const {
    loading: parentEntityLoading,
    hasNextPage: hasMoreParentEntities,
    fetchMore: fetchMoreParentEntities,
    data: parentEntityData,
  } = useGetEntitiesBrokeragesOptions({
    pageNumber: 0,
    pageSize: 50,
    isParent: true,
    depthSearch: 1,
    enabled: modalOpen,
    brokerageName: debouncedParentEntityName,
  });

  // Fetch partner entity options with infinite scroll (same list as parent)
  const {
    loading: partnerEntityLoading,
    hasNextPage: hasMorePartnerEntities,
    fetchMore: fetchMorePartnerEntities,
    data: partnerEntityData,
  } = useGetEntitiesBrokeragesOptions({
    pageNumber: 0,
    pageSize: 50,
    isParent: true,
    depthSearch: 1,
    enabled: modalOpen,
    brokerageName: debouncedPartnerEntityName,
  });

  // UW Regions (static, fetched once)
  const { data: uwRegions } = useGetRegion();

  const UNDERWRITING_REGION_OPTIONS = useMemo(() => {
    return (
      uwRegions?.uwTerritories?.edges?.map((parentOption) => ({
        value: parentOption?.node?.uuid || '',
        label: parentOption?.node?.name || '',
      })) || []
    );
  }, [uwRegions]);

  const transformBrokerageData = (data: typeof brokerageData) => {
    if (!data?.entities?.edges) return [];
    return data.entities.edges.map((edge) => {
      const node = edge.node;
      const aliases = node.aliases?.edges || [];
      const legalNameAlias = aliases.find(
        (a) => a.node.primary && a.node.type === 'LEGAL_NAME'
      );
      const tradingNameAlias = aliases.find(
        (a) => a.node.primary && a.node.type === 'TRADING_NAME'
      );
      let label = node.referenceId || 'N/A';
      if (legalNameAlias?.node.name?.trim()) {
        label = legalNameAlias.node.name;
      } else if (tradingNameAlias?.node.name) {
        label = tradingNameAlias.node.name;
      }
      return { value: node.uuid || '-', label };
    });
  };

  // Infinite scroll for brokerage
  const {
    options: allBrokerages,
    loadingMore: loadingMoreBrokerages,
    isPending: isBrokeragePending,
    handleScroll: handleBrokerageScroll,
  } = useInfiniteScrollFilter({
    data: brokerageData,
    loading: brokerageListLoading,
    hasNextPage: hasMoreBrokerages,
    fetchMore: fetchMoreBrokerages,
    transformData: transformBrokerageData,
    searchTerm: debouncedBrokerageName,
    selectedValues: pendingFilters['brokerage'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => {
      const aliasFilters: any = { source: 'PRIMARY' };
      if (searchTerm) {
        aliasFilters.name = searchTerm;
      }
      return {
        pagination: { after: pageSize * nextPage, first: pageSize },
        filters: {
          agency: true,
          ...(searchTerm && { brokerageName: searchTerm }),
        },
        aliasFilters,
      };
    },
  });

  // Infinite scroll for parent entity
  const {
    options: allParentEntities,
    loadingMore: loadingMoreParentEntities,
    isPending: isParentEntityPending,
    handleScroll: handleParentEntityScroll,
  } = useInfiniteScrollFilter({
    data: parentEntityData,
    loading: parentEntityLoading,
    hasNextPage: hasMoreParentEntities,
    fetchMore: fetchMoreParentEntities,
    transformData: transformBrokerageData,
    searchTerm: debouncedParentEntityName,
    selectedValues: pendingFilters['parentEntity'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => {
      const aliasFilters: any = { source: 'PRIMARY' };
      if (searchTerm) {
        aliasFilters.name = searchTerm;
      }
      return {
        pagination: { after: pageSize * nextPage, first: pageSize },
        filters: {
          agency: true,
          parents: true,
          depthSearch: 1,
          ...(searchTerm && { brokerageName: searchTerm }),
        },
        aliasFilters,
      };
    },
  });

  // Infinite scroll for partner entity (same query as parent entity)
  const {
    options: allPartnerEntities,
    loadingMore: loadingMorePartnerEntities,
    isPending: isPartnerEntityPending,
    handleScroll: handlePartnerEntityScroll,
  } = useInfiniteScrollFilter({
    data: partnerEntityData,
    loading: partnerEntityLoading,
    hasNextPage: hasMorePartnerEntities,
    fetchMore: fetchMorePartnerEntities,
    transformData: transformBrokerageData,
    searchTerm: debouncedPartnerEntityName,
    selectedValues: pendingFilters['partnerEntity'] || [],
    buildFetchMoreVariables: (nextPage, searchTerm, pageSize) => {
      const aliasFilters: any = { source: 'PRIMARY' };
      if (searchTerm) {
        aliasFilters.name = searchTerm;
      }
      return {
        pagination: { after: pageSize * nextPage, first: pageSize },
        filters: {
          agency: true,
          parents: true,
          depthSearch: 1,
          ...(searchTerm && { brokerageName: searchTerm }),
        },
        aliasFilters,
      };
    },
  });

  // Update brokerage options from infinite scroll
  useEffect(() => {
    setFilterOptions((prev) => ({ ...prev, brokerage: allBrokerages }));
    setAllOptions((prev) => ({ ...prev, brokerage: allBrokerages }));
    if (setDefaultBrokerages && allBrokerages.length) {
      setDefaultBrokerages(allBrokerages);
    }
  }, [allBrokerages]);

  // Update parent entity options from infinite scroll
  useEffect(() => {
    setFilterOptions((prev) => ({ ...prev, parentEntity: allParentEntities }));
    setAllOptions((prev) => ({ ...prev, parentEntity: allParentEntities }));
  }, [allParentEntities]);

  // Update partner entity options from infinite scroll
  useEffect(() => {
    setFilterOptions((prev) => ({
      ...prev,
      partnerEntity: allPartnerEntities,
    }));
    setAllOptions((prev) => ({ ...prev, partnerEntity: allPartnerEntities }));
  }, [allPartnerEntities]);

  useEffect(() => {
    if (UNDERWRITING_REGION_OPTIONS.length) {
      setAllOptions((prev) => ({
        ...prev,
        uwRegion: UNDERWRITING_REGION_OPTIONS,
        status: statusOptions,
      }));
      setFilterOptions((prev) => ({
        ...prev,
        uwRegion: UNDERWRITING_REGION_OPTIONS,
        status: statusOptions,
      }));
    }
  }, [uwRegions]);

  const handleModalOpen = () => {
    setModalOpen(true);
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);
    setSearchFilters({
      brokerage: '',
      parentEntity: '',
      partnerEntity: '',
      uwRegion: '',
      status: '',
    });
    try {
      const saved = currentFilters['onboardedDate'];
      if (saved?.length > 0) {
        const parsed = JSON.parse(saved[0]);
        setOnboardedDateRange([
          parsed[0] ? dayjs(parsed[0]) : null,
          parsed[1] ? dayjs(parsed[1]) : null,
        ]);
      } else {
        setOnboardedDateRange([null, null]);
      }
    } catch {
      setOnboardedDateRange([null, null]);
    }
    setOnboardedDateError('');
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSearchFilters({
      brokerage: '',
      parentEntity: '',
      partnerEntity: '',
      uwRegion: '',
      status: '',
    });
    setPendingFilters(currentFilters);
    setPendingFilterLabels(currentFilterLabels);
    setOnboardedDateRange([null, null]);
    setOnboardedDateError('');
  };

  const handleSaveFilter = () => {
    setCurrentFilters(pendingFilters);
    setCurrentFilterLabels(pendingFilterLabels);
    setFilterLabels(pendingFilterLabels);
    setBrokerageFilters && setBrokerageFilters(pendingFilters);
    localStorage.setItem('brokerageFilters', JSON.stringify(pendingFilters));
    localStorage.setItem(
      'brokerageFilterLabels',
      JSON.stringify(pendingFilterLabels)
    );
    resetPageNumber();
    handleModalClose();
  };

  const handleClearAllFilters = () => {
    setCurrentFilters(defaultFilters);
    setCurrentFilterLabels(defaultFilters);
    setFilterLabels(defaultFilters);
    setPendingFilters(defaultFilters);
    setPendingFilterLabels(defaultFilters);
    setBrokerageFilters && setBrokerageFilters(defaultFilters);
    setOnboardedDateRange([null, null]);
    setOnboardedDateError('');
    localStorage.removeItem('brokerageFilters');
    localStorage.removeItem('brokerageFilterLabels');
    resetPageNumber();
  };

  const handleCheckboxChange = (
    column: keyof BrokerageFilterOptions,
    option: Option
  ) => {
    const currentChecked = Array.isArray(pendingFilters[column])
      ? pendingFilters[column]
      : [];
    const currentLabels = Array.isArray(pendingFilterLabels[column])
      ? pendingFilterLabels[column]
      : [];

    let updatedFilter: string[];
    let updatedFilterLabel: string[];

    if (currentChecked.includes(option.value)) {
      updatedFilter = currentChecked.filter((item) => item !== option.value);
      updatedFilterLabel = currentLabels.filter(
        (label) => label !== option.label
      );
    } else {
      updatedFilter = [...currentChecked, option.value];
      updatedFilterLabel = [...currentLabels, option.label];
    }

    setPendingFilters({ ...pendingFilters, [column]: updatedFilter });
    setPendingFilterLabels({
      ...pendingFilterLabels,
      [column]: updatedFilterLabel,
    });
  };

  const handleAccordionChange = (panel: string) => {
    setExpandedPanels({ ...expandedPanels, [panel]: !expandedPanels[panel] });
  };

  const renderActiveFilters = () => {
    const activeFilters = Object.keys(currentFilterLabels).filter(
      (key) => currentFilterLabels[key]?.length > 0
    );

    if (activeFilters.length === 0) return null;

    return (
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', ml: 2 }}>
        {activeFilters.map((filterKey) => {
          const labels = currentFilterLabels[filterKey] || [];
          return labels.map((label: string, index: number) => (
            <Box
              key={`${filterKey}-${index}`}
              sx={{ display: 'flex', alignItems: 'center', mr: '22px' }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, fontSize: '14px' }}
              >
                {label}
              </Typography>
              <Cancel
                sx={{ ml: 1, fontSize: '16px', cursor: 'pointer' }}
                onClick={(e) => {
                  e.stopPropagation();

                  const optionToRemove = allOptions[
                    filterKey as keyof BrokerageFilterOptions
                  ]?.find((opt: Option) => opt.label === label);

                  let valueToRemove: string | undefined;
                  if (optionToRemove) {
                    valueToRemove = optionToRemove.value;
                  } else {
                    const labelIndex = (
                      currentFilterLabels[filterKey] || []
                    ).indexOf(label);
                    if (labelIndex !== -1) {
                      valueToRemove = currentFilters[filterKey]?.[labelIndex];
                    }
                  }

                  if (valueToRemove) {
                    const updatedFilters = {
                      ...currentFilters,
                      [filterKey]: (currentFilters[filterKey] || []).filter(
                        (v) => v !== valueToRemove
                      ),
                    };
                    const updatedLabels = {
                      ...currentFilterLabels,
                      [filterKey]: (
                        currentFilterLabels[filterKey] || []
                      ).filter((l) => l !== label),
                    };
                    setCurrentFilters(updatedFilters);
                    setCurrentFilterLabels(updatedLabels);
                    setFilterLabels(updatedLabels);
                    setPendingFilters(updatedFilters);
                    setPendingFilterLabels(updatedLabels);
                    setBrokerageFilters && setBrokerageFilters(updatedFilters);
                    localStorage.setItem(
                      'brokerageFilters',
                      JSON.stringify(updatedFilters)
                    );
                    localStorage.setItem(
                      'brokerageFilterLabels',
                      JSON.stringify(updatedLabels)
                    );
                    resetPageNumber();
                  }
                }}
              />
            </Box>
          ));
        })}
      </Box>
    );
  };

  const renderOnboardedDateAccordion = () => {
    const displayLabel = 'Onboarded Date';

    const handleFromChange = (newValue: Dayjs | null) => {
      const to = onboardedDateRange[1];
      setOnboardedDateRange([newValue, to]);
      if ((newValue && !to) || (!newValue && to)) {
        setOnboardedDateError('Both date fields are required');
        setPendingFilters({ ...pendingFilters, onboardedDate: [] });
      } else if (newValue && to) {
        if (newValue.isAfter(to)) {
          setOnboardedDateError('From date cannot be after To date');
          setPendingFilters({ ...pendingFilters, onboardedDate: [] });
        } else {
          setOnboardedDateError('');
          setPendingFilters({
            ...pendingFilters,
            onboardedDate: [
              JSON.stringify([newValue.toISOString(), to.toISOString()]),
            ],
          });
          setPendingFilterLabels({
            ...pendingFilterLabels,
            onboardedDate: [
              `${newValue.format('MM/DD/YYYY')} - ${to.format('MM/DD/YYYY')}`,
            ],
          });
        }
      } else {
        setOnboardedDateError('');
        setPendingFilters({ ...pendingFilters, onboardedDate: [] });
        setPendingFilterLabels({ ...pendingFilterLabels, onboardedDate: [] });
      }
    };

    const handleToChange = (newValue: Dayjs | null) => {
      const from = onboardedDateRange[0];
      setOnboardedDateRange([from, newValue]);
      if ((from && !newValue) || (!from && newValue)) {
        setOnboardedDateError('Both date fields are required');
        setPendingFilters({ ...pendingFilters, onboardedDate: [] });
      } else if (from && newValue) {
        if (from.isAfter(newValue)) {
          setOnboardedDateError('From date cannot be after To date');
          setPendingFilters({ ...pendingFilters, onboardedDate: [] });
        } else {
          setOnboardedDateError('');
          setPendingFilters({
            ...pendingFilters,
            onboardedDate: [
              JSON.stringify([from.toISOString(), newValue.toISOString()]),
            ],
          });
          setPendingFilterLabels({
            ...pendingFilterLabels,
            onboardedDate: [
              `${from.format('MM/DD/YYYY')} - ${newValue.format('MM/DD/YYYY')}`,
            ],
          });
        }
      } else {
        setOnboardedDateError('');
        setPendingFilters({ ...pendingFilters, onboardedDate: [] });
        setPendingFilterLabels({ ...pendingFilterLabels, onboardedDate: [] });
      }
    };

    return (
      <Accordion
        expanded={expandedPanels['onboardedDate']}
        onChange={() => handleAccordionChange('onboardedDate')}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': { margin: '0 !important' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            minHeight: '56px',
            height: '56px',
            '& .MuiAccordionSummary-content': { margin: '0' },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box
              sx={{
                marginLeft: '38px',
                width: '87%',
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 1,
                    color: 'black',
                    textTransform: 'uppercase',
                  }}
                >
                  From
                </Typography>
                <Box
                  sx={{
                    border: '1px solid black',
                    borderRadius: '4px',
                    '&:hover': { borderColor: '#28c0a7' },
                    '&:focus-within': {
                      borderColor: '#28c0a7',
                      borderWidth: '2px',
                    },
                  }}
                >
                  <DatePicker
                    value={onboardedDateRange[0]}
                    maxDate={onboardedDateRange[1] || undefined}
                    onChange={handleFromChange}
                    components={{ OpenPickerIcon: DateRangeIcon }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{ ...params.inputProps, placeholder: '' }}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px',
                            fontWeight: '600',
                            '& fieldset': { border: 'none' },
                            '& .MuiIconButton-root': { color: 'black' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    mb: 1,
                    color: 'black',
                    textTransform: 'uppercase',
                  }}
                >
                  To
                </Typography>
                <Box
                  sx={{
                    border: '1px solid black',
                    borderRadius: '4px',
                    '&:hover': { borderColor: '#28c0a7' },
                    '&:focus-within': {
                      borderColor: '#28c0a7',
                      borderWidth: '2px',
                    },
                  }}
                >
                  <DatePicker
                    value={onboardedDateRange[1]}
                    minDate={onboardedDateRange[0] || undefined}
                    onChange={handleToChange}
                    components={{ OpenPickerIcon: DateRangeIcon }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        inputProps={{ ...params.inputProps, placeholder: '' }}
                        size="small"
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: '14px',
                            fontWeight: '600',
                            '& fieldset': { border: 'none' },
                            '& .MuiIconButton-root': { color: 'black' },
                          },
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
            {onboardedDateError && (
              <Typography
                sx={{ color: '#d32f2f', fontSize: '12px', ml: '38px', mt: 1 }}
              >
                {onboardedDateError}
              </Typography>
            )}
          </LocalizationProvider>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderFilterAccordion = (
    column: keyof BrokerageFilterOptions,
    label: string
  ) => {
    const isInfiniteScroll =
      column === 'brokerage' ||
      column === 'parentEntity' ||
      column === 'partnerEntity';
    const columnLoadingMap: Partial<
      Record<keyof BrokerageFilterOptions, boolean>
    > = {
      brokerage: brokerageListLoading || isBrokeragePending,
      parentEntity: parentEntityLoading || isParentEntityPending,
      partnerEntity: partnerEntityLoading || isPartnerEntityPending,
    };
    const isColumnLoading = columnLoadingMap[column] ?? false;
    const selectedCount = pendingFilters[column]?.length || 0;
    const displayLabel =
      selectedCount > 0 ? `${label} (${selectedCount})` : label;
    const currentColumnOptions = filterOptions[column] || [];

    return (
      <Accordion
        key={column}
        expanded={expandedPanels[column]}
        onChange={() => handleAccordionChange(column)}
        sx={{
          boxShadow: 'none',
          '&:before': { display: 'none' },
          borderBottom: '1px solid #e0e0e0',
          margin: '0 !important',
          '&.Mui-expanded': { margin: '0 !important' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            minHeight: '56px',
            height: '56px',
            '& .MuiAccordionSummary-content': { margin: '0' },
            '& .MuiAccordionSummary-expandIconWrapper': {
              order: -1,
              marginRight: '12px',
              color: 'black',
              transform: 'rotate(-90deg)',
              transition: 'transform 0.3s',
            },
            '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
              transform: 'rotate(0deg)',
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '14px',
              textTransform: 'uppercase',
              letterSpacing: '.5px',
            }}
          >
            {displayLabel}
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ marginLeft: '38px', mb: 2, width: '85%' }}>
            <Autocomplete
              size="small"
              fullWidth
              multiple
              disableClearable={isInfiniteScroll}
              freeSolo={true}
              filterOptions={(x) => x}
              options={[]}
              open={false}
              getOptionLabel={(option): string => {
                if (typeof option === 'string') return option;
                if (option && typeof option === 'object' && 'label' in option) {
                  return String(option.label || '');
                }
                return '';
              }}
              value={[] as Option[]}
              isOptionEqualToValue={(
                option: Option | string,
                value: Option | string
              ) => {
                if (!option || !value) return false;
                if (typeof option === 'string' || typeof value === 'string')
                  return false;
                if (Array.isArray(option) || Array.isArray(value)) return false;
                return (option as Option).value === (value as Option).value;
              }}
              onChange={() => {
                /* selection handled via checkboxes below */
              }}
              inputValue={searchFilters[column]}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'reset') return;

                if (reason === 'clear') {
                  if (isInfiniteScroll) return;
                  setSearchFilters({ ...searchFilters, [column]: '' });
                  setFilterOptions((prev) => ({
                    ...prev,
                    [column]: allOptions[column],
                  }));
                  return;
                }

                setSearchFilters({
                  ...searchFilters,
                  [column]: newInputValue,
                });

                if (!isInfiniteScroll) {
                  if (newInputValue.trim()) {
                    const filtered = allOptions[column]?.filter((opt) =>
                      opt?.label
                        ?.toLowerCase()
                        .includes(newInputValue.toLowerCase())
                    );
                    setFilterOptions((prev) => ({
                      ...prev,
                      [column]: filtered,
                    }));
                  } else {
                    setFilterOptions((prev) => ({
                      ...prev,
                      [column]: allOptions[column],
                    }));
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    'data-testid': `${column}-search-input`,
                  }}
                  placeholder={`Search ${label}`}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      fontSize: '14px',
                      fontWeight: '600',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'black',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#28c0a7',
                        borderWidth: '2px',
                      },
                    },
                  }}
                />
              )}
              sx={{
                '& .MuiAutocomplete-popupIndicator': { display: 'none' },
                '& .MuiAutocomplete-clearIndicator': {
                  display:
                    isInfiniteScroll ||
                    column === 'uwRegion' ||
                    column === 'status'
                      ? 'none'
                      : 'block',
                  visibility: 'visible',
                  color: 'black',
                },
              }}
            />
          </Box>
          <Box
            sx={{
              marginLeft: '30px',
              width: '85%',
              maxHeight: '250px',
              overflow: 'auto',
            }}
            onScroll={
              column === 'brokerage'
                ? handleBrokerageScroll
                : column === 'parentEntity'
                ? handleParentEntityScroll
                : column === 'partnerEntity'
                ? handlePartnerEntityScroll
                : undefined
            }
          >
            <FormGroup>
              {isColumnLoading && currentColumnOptions.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#474343' }}>
                  Loading...
                </Typography>
              ) : currentColumnOptions.length > 0 ? (
                currentColumnOptions.map((option, index) => {
                  const isChecked = pendingFilters[column]?.includes(
                    option.value
                  );
                  return (
                    <FormControlLabel
                      key={`${option.value}-${index}`}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleCheckboxChange(column, option)}
                          inputProps={
                            {
                              'data-testid': `${column}-checkbox_${index + 1}`,
                            } as React.InputHTMLAttributes<HTMLInputElement>
                          }
                          size="small"
                          sx={{
                            alignSelf: 'flex-start',
                            paddingTop: '2px',
                            color: '#666666',
                            '&.Mui-checked': { color: '#28C0A7' },
                          }}
                        />
                      }
                      label={highlightTextForFilter(
                        option.label,
                        searchFilters[column]
                      )}
                      sx={{
                        alignItems: 'flex-start',
                        margin: '2px 0',
                        '& .MuiFormControlLabel-label': {
                          fontWeight: '600',
                          fontSize: '14px',
                          paddingTop: '3px',
                        },
                      }}
                    />
                  );
                })
              ) : (
                <Typography variant="body2" sx={{ color: '#474343' }}>
                  No results
                </Typography>
              )}
              {column === 'brokerage' && loadingMoreBrokerages && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
              {column === 'parentEntity' && loadingMoreParentEntities && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
              {column === 'partnerEntity' && loadingMorePartnerEntities && (
                <Typography
                  variant="body2"
                  sx={{ color: '#666', textAlign: 'center', mt: 1, mb: 1 }}
                >
                  Loading more...
                </Typography>
              )}
            </FormGroup>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <>
      <Box
        className="filter-buttons"
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '28px',
          flexWrap: 'wrap',
          color: 'black',
        }}
      >
        <Box
          data-testid="open-filters-button"
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            border: '1px solid black',
            borderRadius: '4px',
            marginRight: '10px',
          }}
          onClick={handleModalOpen}
        >
          <FilterList sx={{ padding: '5px 5px' }} />
        </Box>

        {renderActiveFilters()}
        {Object.keys(currentFilterLabels).some(
          (key) => currentFilterLabels[key]?.length > 0
        ) && (
          <Button
            size="small"
            data-testid="clear-all-filters-button"
            onClick={handleClearAllFilters}
            disableRipple
            sx={{
              ml: 0,
              textAlign: 'left',
              color: 'black',
              textDecoration: 'underline',
              fontSize: '14px',
              fontWeight: 600,
              backgroundColor: 'transparent',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:focus': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&:active': {
                backgroundColor: 'transparent',
                textDecoration: 'underline',
              },
              '&.Mui-focusVisible': { backgroundColor: 'transparent' },
            }}
          >
            Clear all filters
          </Button>
        )}
      </Box>

      <Drawer anchor="left" open={modalOpen} onClose={handleModalClose}>
        <Box
          sx={{
            height: '100vh',
            width: 400,
            bgcolor: 'background.paper',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ overflow: 'auto', flex: 1 }}>
            {renderFilterAccordion('brokerage', 'Brokerage')}
            {renderFilterAccordion('parentEntity', 'Parent Entity')}
            {renderFilterAccordion('partnerEntity', 'Partner Entity')}
            {renderFilterAccordion('uwRegion', 'Underwriting Region')}
            {renderOnboardedDateAccordion()}
            {renderFilterAccordion('status', 'Status')}
          </Box>

          <Box
            sx={{
              p: 1,
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              gap: 3,
              height: '39px',
            }}
          >
            <Box style={{ marginLeft: '50px' }}>
              <NewConvergeButton
                variant="outlined"
                label=" Cancel"
                onClick={handleModalClose}
                data-testid="cancel-filters-button"
              />
            </Box>
            <Box sx={{ width: '137px' }}>
              <NewConvergeButton
                variant="contained"
                onClick={handleSaveFilter}
                label=" Save Filters"
                data-testid="save-filters-button"
              />
            </Box>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default BrokerageFilter;
